import { Box, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { LoginFormData } from '../Login.types';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { StyledInput } from '../Login.styles';
import { Button, Checkbox, PasswordInput, Text, Flex } from '@components';
import { useEffect } from 'react';
import { setThemeColor } from '@utils/metaTags/setThemeColor';

export interface LoginFormProps {
  onSubmit: (data: LoginFormData) => void;
}

export const LoginForm = ({ onSubmit }: LoginFormProps): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    setThemeColor(theme.palette.common.white);

    return () => setThemeColor(theme.palette.primary.dark);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    watch,
  } = useFormContext<LoginFormData>();

  const email = watch('emailAddress');

  useEffect(() => {
    const email = getValues('emailAddress');
    if (email && email?.indexOf(' ') > -1) {
      setValue('emailAddress', email?.trim());
      trigger('emailAddress');
    }
  }, [email, trigger, setValue, getValues]);

  return (
    <form
      style={{
        marginTop: theme.spacing(8),
        width: '100%',
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Flex gap={16} flexDirection='column'>
        <Flex flexDirection='column' gap={16}>
          <StyledInput
            label={intl.formatMessage({
              id: 'misc.emailAddress',
            })}
            autoFocus
            fullWidth
            inputProps={{
              'data-testid': 'signin-email-address',
            }}
            autoComplete='email'
            type='email'
            {...registerInput(register, 'emailAddress')}
            {...getErrorProps(intl, errors.emailAddress)}
          />
          <PasswordInput
            label={intl.formatMessage({
              id: 'misc.password',
            })}
            fullWidth
            inputProps={{
              'data-testid': 'signin-password',
            }}
            autoComplete='current-password'
            {...registerInput(register, 'password')}
            {...getErrorProps(intl, errors.password)}
          />
        </Flex>
        <Flex flexDirection='row' justifyContent='space-between'>
          <Checkbox
            label={
              <Text
                variant='h6'
                fontWeight='fontWeightMedium'
                style={{ color: theme.palette.text.primary }}
              >
                <FormattedMessage id='login.keepMeLoggedIn' />
              </Text>
            }
            inputProps={{
              'data-testid': 'signin-keep-me-logged-in',
            }}
            {...registerInput(register, 'keepUserLoggedIn')}
          />
          <Button
            data-testid='forgot-password-button'
            variant='tertiary'
            color='primary'
            style={{ padding: 0 }}
            onClick={() => navigate('/reset-password')}
          >
            <FormattedMessage id='misc.forgotPassword' />?
          </Button>
        </Flex>
        <Box>
          <Button
            data-testid='continue-button'
            fullWidth
            variant='primary'
            size='large'
            type='submit'
            color='primary'
          >
            <FormattedMessage id='misc.signIn' />
          </Button>
        </Box>
      </Flex>
    </form>
  );
};
